import React, { useState } from 'react'
import { Platform, Pressable, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'

import { Text } from '../../component/typography'
import { Color, Spacing } from '../../style'

import { HelpIcon, HelpPointer } from '../help'

import { Banner } from '../feedback'

import Sort from './sort'
import { TableCol, TableSort } from './table'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: Spacing.S,
    paddingVertical: Spacing.XS,
    minHeight: 42,
    alignItems: 'center',
  },
  cell: {
    flex: 1,
    marginHorizontal: Spacing.XXS,
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    // width: '100%',
    color: Color.metalGrey,
  },
  selected: {
    color: Color.premiumBlack,
  },
  active: {
    color: Color.greyBlue,
  },
  accessory: {
    width: 10,
  },
  help: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: Spacing.S,
    marginVertical: 0,
  },
  helpIcon: {
    marginHorizontal: Spacing.XS,
  },
  hidden: {
    height: 0,
    lineHeight: 0,
    overflow: 'hidden',
  },
  helpPointer: {
    marginBottom: 2,
    marginLeft: Spacing.XXS,
    transform: [{ rotate: '180deg' }],
  },
})

interface Props extends ViewProps {
  cols: TableCol[]
  sort?: TableSort
  defaultSort?: TableSort
  onSort?: (sort: TableSort) => void
  hasAccessory?: boolean
}
const Head = (props: Props) => {
  const { cols, sort: propsSort, defaultSort, onSort, hasAccessory, style, ...otherProps } = props
  const [sort, setSort] = useState(propsSort || defaultSort)
  const [active, setActive] = useState<string>()
  const [showHelp, setShowHelp] = useState<number>()

  const updateActive = (value: boolean, col: string) => () => {
    if (value) setActive(col)
    else if (active === col) setActive(undefined)
  }

  const updateSort = (col: TableCol) => () => {
    const newSort: TableSort = {
      field: col.key,
      direction: sort?.field === col.key && sort?.direction !== 'desc' ? 'desc' : 'asc',
    }
    if (propsSort === undefined) {
      setSort(newSort)
    }
    onSort && onSort(newSort)
  }

  function renderHelp() {
    return (
      showHelp !== undefined && (
        <>
          <Banner message={cols[showHelp]?.help} status="help" onClose={() => setShowHelp(undefined)} closeable />
          <View style={styles.help} {...otherProps}>
            {cols.map((col, index) => {
              const sizeStyle: (ViewStyle | false)[] = [
                !!col.size?.width && { maxWidth: col.size?.width },
                !!col.size?.flex && { flex: col.size?.flex },
              ]
              const { key, title } = col
              return (
                <View key={col.key} style={[styles.cell, sizeStyle]}>
                  {title && typeof title !== 'string' ? (
                    <View style={styles.hidden}>{title}</View>
                  ) : (
                    <Text level={7} style={[styles.text, styles.hidden]} numberOfLines={1}>
                      {((title || key) as string).toUpperCase()}
                    </Text>
                  )}
                  {showHelp === index && <HelpPointer style={[styles.helpIcon, styles.helpPointer]} />}
                </View>
              )
            })}
            {hasAccessory && <View style={styles.accessory} />}
          </View>
        </>
      )
    )
  }

  const renderCol = (col: TableCol, index: number) => () => {
    const { key, title, help } = col
    if (title === null) return null

    const textStyle = [styles.text, sort?.field === key && styles.selected, active === key && styles.active]
    return (
      <>
        {title && typeof title !== 'string' ? (
          title
        ) : (
          <Text level={7} style={textStyle} numberOfLines={1}>
            {((title || key) as string).toUpperCase()}
          </Text>
        )}
        {help && (
          <HelpIcon style={styles.helpIcon} onPress={() => setShowHelp(showHelp === index ? undefined : index)} />
        )}
        {col.sort !== false && (
          <Sort
            direction={sort?.field === key ? sort?.direction || 'asc' : undefined}
            iconStyle={sort?.field !== key && active === key && styles.active}
            activeStyle={active === key && styles.active}
          />
        )}
      </>
    )
  }

  return (
    <>
      {renderHelp()}
      <View style={[styles.container, style]} {...otherProps}>
        {cols.map((col, index) => {
          const sizeStyle: (ViewStyle | false)[] = [
            !!col.size?.width && { maxWidth: col.size?.width },
            !!col.size?.flex && { flex: col.size?.flex },
          ]
          return (
            <Pressable
              key={col.key}
              onFocus={updateActive(true, col.key)}
              onBlur={updateActive(false, col.key)}
              style={[
                Platform.select({
                  // @ts-expect-error outline style none is not defined
                  web: {
                    outlineStyle: 'none',
                  } as ViewStyle,
                }),
                styles.cell,
                sizeStyle,
              ]}
              onPress={updateSort(col)}
              testID="TableHead.Container"
            >
              {renderCol(col, index)}
            </Pressable>
          )
        })}
        {hasAccessory && <View style={styles.accessory} />}
      </View>
    </>
  )
}

export default Head
